import * as Yup from "yup";

import { Box, Button, TextField, Typography, styled } from "@mui/material";

import { register } from "../../stores/auth/authSlice";
import { selectUser } from "../../stores/auth/authSelectors";
import { useDispatch } from "../../stores";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const Register = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector(selectUser);

    useEffect(() => {
        if (user.user) {
            navigate("/home");
        }
    }, [user, navigate]);

    const formik = useFormik({
        initialValues: {
            firstName: "",
            lastName: "",
            email: "",
            password: "",
            confirmPassword: "",
        },
        validationSchema: Yup.object({
            firstName: Yup.string().required("First name is required"),
            lastName: Yup.string().required("Last name is required"),
            email: Yup.string()
                .email("Invalid email")
                .required("Email is required"),
            password: Yup.string()
                .min(6, "Password must be at least 6 characters")
                .required("Password is required"),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref("password")], "Passwords must match")
                .required("Confirm password is required"),
        }),
        onSubmit: async (values, { setErrors }) => {
            try {
                await dispatch(register(values)).unwrap();
                navigate("/home");
            } catch (error: any) {
                setErrors({ email: error.message || "Registration failed" });
            }
        },
    });

    return (
        <PageWrapper>
            <FormContainer>
                <Typography variant="h4">Register</Typography>
                <form onSubmit={formik.handleSubmit}>
                    <StyledTextField
                        fullWidth
                        label="First Name"
                        {...formik.getFieldProps("firstName")}
                        error={
                            formik.touched.firstName &&
                            Boolean(formik.errors.firstName)
                        }
                        helperText={
                            formik.touched.firstName && formik.errors.firstName
                        }
                    />
                    <StyledTextField
                        fullWidth
                        label="Last Name"
                        {...formik.getFieldProps("lastName")}
                        error={
                            formik.touched.lastName &&
                            Boolean(formik.errors.lastName)
                        }
                        helperText={
                            formik.touched.lastName && formik.errors.lastName
                        }
                    />
                    <StyledTextField
                        fullWidth
                        label="Email"
                        {...formik.getFieldProps("email")}
                        error={
                            formik.touched.email && Boolean(formik.errors.email)
                        }
                        helperText={formik.touched.email && formik.errors.email}
                    />
                    <StyledTextField
                        fullWidth
                        label="Password"
                        type="password"
                        {...formik.getFieldProps("password")}
                        error={
                            formik.touched.password &&
                            Boolean(formik.errors.password)
                        }
                        helperText={
                            formik.touched.password && formik.errors.password
                        }
                    />
                    <StyledTextField
                        fullWidth
                        label="Confirm Password"
                        type="password"
                        {...formik.getFieldProps("confirmPassword")}
                        error={
                            formik.touched.confirmPassword &&
                            Boolean(formik.errors.confirmPassword)
                        }
                        helperText={
                            formik.touched.confirmPassword &&
                            formik.errors.confirmPassword
                        }
                    />
                    <StyledButton
                        type="submit"
                        variant="contained"
                        disabled={!user}
                    >
                        {!user ? "Registering..." : "Register"}
                    </StyledButton>
                </form>

                <Typography variant="body2">
                    Already have an account?{" "}
                    <StyledLink onClick={() => navigate("/login")}>
                        Login
                    </StyledLink>
                </Typography>
            </FormContainer>
        </PageWrapper>
    );
};

export default Register;

// Styled Components
const PageWrapper = styled(Box)({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    backgroundImage: "url(/assets/tawhadi_background.png)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "repeat",
});

const FormContainer = styled(Box)({
    width: "100%",
    maxWidth: 400,
    padding: "2rem",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    borderRadius: "8px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
});

const StyledTextField = styled(TextField)({
    marginBottom: "1rem",
});

const StyledButton = styled(Button)({
    width: "100%",
    padding: "0.8rem",
    marginTop: "1rem",
});

const StyledLink = styled("span")({
    color: "blue",
    cursor: "pointer",
    textDecoration: "underline",
});
